
"use client";

import type React from "react";
import { useState } from "react";
import emailjs from "@emailjs/browser";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { ScrollReveal } from "@/components/animations/scroll-reveal";
import { StudentTestimonials } from "@/components/student-testimonials";
import Image from "next/image";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  enquiryType: "general" | "offline-training";
  subject: string;
  course: string;
  location: string;
  preferredDate: string;
  groupSize: string;
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    enquiryType: "general",
    subject: "",
    course: "",
    location: "",
    preferredDate: "",
    groupSize: "1",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setLoading(true);
  //   console.log("[Contact Form Submitted]", formData);

  //   try {
  //     const result = await emailjs.send(
  //       "service_a13hscr", // Replace with your actual Service ID
  //       "template_whz81ci", // Replace with your actual Template ID
  //       {
  //         from_name: formData.name,
  //         from_email: formData.email,
  //         phone: formData.phone,
  //         subject: formData.subject || "No Subject",
  //         course: formData.course,
  //         location: formData.location,
  //         preferred_date: formData.preferredDate,
  //         group_size: formData.groupSize,
  //         enquiry_type: formData.enquiryType,
  //         message: formData.message,
  //       },
  //       "mCiLyWXFq97oSJtU1" // Replace with your actual EmailJS Public Key
  //     );

  //     console.log("Email sent successfully:", result.text);
  //     setSubmitted(true);

  //     // Reset form
  //     setFormData({
  //       name: "",
  //       email: "",
  //       phone: "",
  //       enquiryType: "general",
  //       subject: "",
  //       course: "",
  //       location: "",
  //       preferredDate: "",
  //       groupSize: "1",
  //       message: "",
  //     });

  //     setTimeout(() => setSubmitted(false), 3000);
  //   } catch (error: any) {
  //     console.error("EmailJS Error:", error);
  //     alert("Failed to send message. Please try again later.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const COURSES = [
  //   "JavaScript Fundamentals",
  //   "React.js Masterclass",
  //   "Python for Data Science",
  //   "Advanced TypeScript",
  //   "Web Design with CSS",
  //   "Node.js Backend Development",
  //   "Vue.js Framework",
  //   "Full Stack Web Development",
  // ];
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  // 🧠 Prevent multiple clicks / double emails
  if (loading) return;
  setLoading(true);

  console.log("[Contact Form Submitted]", formData);

  try {
    // 🧩 Build the email template parameters
    const templateParams =
      formData.enquiryType === "offline-training"
        ? {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone,
            enquiry_type: "Offline Training",
            course: formData.course,
            location: formData.location,
            preferred_date: formData.preferredDate,
            group_size: formData.groupSize,
            message: formData.message || "No additional message provided",
            subject: `Offline Training Enquiry: ${formData.course}`,
          }
        : {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone,
            enquiry_type: "Online / General",
            subject: formData.subject || "General Enquiry",
            message: formData.message || "No message provided",
          };

    console.log("📨 Sending Email with data:", templateParams);

    // 🚀 Send the email using EmailJS
    const result = await emailjs.send(
      "service_a13hscr",   // ✅ Your Service ID
      "template_whz81ci",  // ✅ Your Template ID
      templateParams,
      "mCiLyWXFq97oSJtU1"  // ✅ Your Public Key
    );

    console.log("✅ Email sent successfully:", result.text);
    setSubmitted(true);

    // 🧹 Reset form after success
    setFormData({
      name: "",
      email: "",
      phone: "",
      enquiryType: "general",
      subject: "",
      course: "",
      location: "",
      preferredDate: "",
      groupSize: "1",
      message: "",
    });

    // ⏳ Hide success message after 3 seconds
    setTimeout(() => setSubmitted(false), 3000);

  } catch (error: any) {
    console.error("❌ EmailJS Error:", error);
    alert("Failed to send message. Please try again later.");
  } finally {
    // ✅ Always reset loading state
    setLoading(false);
  }
};



  const COURSES = [
  // --- Web & Full Stack Development ---
  "MERN Stack Web Development",
  "Python Full Stack Web Development",
  "Java Full Stack Development",
  "Full Stack Web Development",
  "Node.js Backend Development",
  "React.js Masterclass",
  "Advanced TypeScript",
  "Vue.js Framework",
  "JavaScript Fundamentals",
  "Web Design with CSS",

  // --- App Development ---
  "React Native App Development",
  "Flutter App Development",

  // --- Programming & DSA ---
  "JAVA (6-Month Program)",
  "C++ | DSA (Multi-Language Mastery Program)",
  "Python for Data Science",

  // --- Cloud & DevOps ---
  "AWS Cloud Engineer",
  "Microsoft Azure Expert",
  "DevOps Mastery",

  // --- Automation & Testing ---
  "RPA Training (UiPath & Automation Anywhere)",
  "Software Testing (6-Month Program)",

  // --- Data & AI ---
  "Data Science & Machine Learning",
  "Data Analytics",

  // --- Security ---
  "Cyber Security (6-Month Program)",

  // --- Design & Creative ---
  "UI/UX Designing & Product Prototyping",
  "Graphic Designing (6-Month Program)",
  "3D Software Architect (6-Month Program)",

  // --- Marketing ---
  "Digital Marketing (6-Month Program)",

  // --- Specialized / Upcoming ---
  "Java Full Stack Development (Advanced)",
  "AI & Deep Learning Engineer",
  "Cloud Native Architecture",
  "Blockchain Development",
  "Game Development with Unity",
  "Others"
];

  const LOCATIONS = ["Virar", "Vasai", "Mumbai"];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header />

     <section
  className="relative min-h-screen bg-blue-400 flex flex-col items-center justify-center text-center overflow-hidden bg-fixed bg-cover bg-center"
  style={{
    backgroundImage: "url('/contact-bg.jpg')", // 🔹 Replace with your image path
  }}
>
  {/* Overlay */}
  <div className="absolute inset-0 bg-black/40" />

  {/* Animated Orbs */}
  <div className="absolute w-96 h-96 bg-blue-400/20 rounded-full blur-3xl top-20 left-10 animate-[float1_10s_ease-in-out_infinite]" />
  <div className="absolute w-[500px] h-[500px] bg-sky-400/20 rounded-full blur-3xl bottom-10 right-10 animate-[float2_12s_ease-in-out_infinite]" />
  <div className="absolute w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-[float3_14s_ease-in-out_infinite]" />

  {/* Animated Grid Pattern */}
  <div className="absolute inset-0 opacity-10">
    <div
      className="absolute inset-0"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
        backgroundSize: "50px 50px",
      }}
    />
  </div>

  {/* Main Content */}
  <div className="relative z-10 px-6 max-w-5xl mx-auto space-y-8">
    {/* Badge */}
    <div className="inline-block animate-fadeInUp">
      <div className="bg-white/10 backdrop-blur-md px-6 py-2 rounded-full border border-white/20 shadow-xl">
        <span className="text-sm md:text-base font-bold text-white flex items-center gap-2">
          <span className="text-xl">💬</span>
          We're Here to Help
        </span>
      </div>
    </div>

    {/* Title */}
    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black bg-gradient-to-r from-blue-200 via-sky-100 to-cyan-200 bg-clip-text text-transparent animate-fadeInUp delay-100 drop-shadow-2xl leading-tight">
      Get in Touch
    </h1>

    {/* Subtitle */}
    <p className="text-xl md:text-3xl text-white/90 font-semibold animate-fadeInUp delay-200 max-w-3xl mx-auto drop-shadow-lg leading-relaxed">
      Have questions? We'd love to hear from you. Reach out today — we're always happy to help.
    </p>

    {/* Feature Pills */}
    <div className="flex flex-wrap justify-center gap-4 pt-4 animate-fadeInUp delay-300">
      <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 text-white font-semibold text-sm md:text-base shadow-lg">
        <span className="flex items-center gap-2">
          ⚡ Quick Response
        </span>
      </div>
      <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 text-white font-semibold text-sm md:text-base shadow-lg">
        <span className="flex items-center gap-2">
          🎯 Expert Support
        </span>
      </div>
      <div className="bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 text-white font-semibold text-sm md:text-base shadow-lg">
        <span className="flex items-center gap-2">
          🌟 24/7 Available
        </span>
      </div>
    </div>

    {/* Contact Methods */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12 max-w-4xl mx-auto animate-fadeInUp delay-400">
      <a
        href="mailto:codifyinstitute.com@gmail.com"
        className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl border border-white/20 shadow-xl hover:bg-white/15 transition-all hover:scale-105 cursor-pointer"
      >
        <div className="text-4xl mb-3">📧</div>
        <h3 className="text-white font-bold text-lg mb-2">Email Us</h3>
        <p className="text-white/80 text-sm break-all">
          codifyinstitute.com@gmail.com
        </p>
      </a>

      <a
        href="tel:+917759020121"
        className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl border border-white/20 shadow-xl hover:bg-white/15 transition-all hover:scale-105 cursor-pointer"
      >
        <div className="text-4xl mb-3">📱</div>
        <h3 className="text-white font-bold text-lg mb-2">Call Us</h3>
        <p className="text-white/80 text-sm">+91 7759020121</p>
      </a>

      <div className="bg-white/10 backdrop-blur-xl p-6 rounded-2xl border border-white/20 shadow-xl hover:bg-white/15 transition-all hover:scale-105">
        <div className="text-4xl mb-3">📍</div>
        <h3 className="text-white font-bold text-lg mb-2">Visit Us</h3>
        <p className="text-white/80 text-sm leading-relaxed">
          Shop No. 201/202, Gold Crest 369,<br />
          Above Zudio, Near New Viva College,<br />
          Virar West, Maharashtra 401303
        </p>
      </div>
    </div>
  </div>

  {/* Animations */}
  <style jsx>{`
    @keyframes float1 {
      0%, 100% {
        transform: translate(0, 0);
      }
      50% {
        transform: translate(-30px, -40px);
      }
    }
    @keyframes float2 {
      0%, 100% {
        transform: translate(0, 0);
      }
      50% {
        transform: translate(25px, 30px);
      }
    }
    @keyframes float3 {
      0%, 100% {
        transform: translate(-50%, -50%);
      }
      50% {
        transform: translate(calc(-50% + 20px), calc(-50% - 25px));
      }
    }
    @keyframes fadeInUp {
      0% {
        opacity: 0;
        transform: translateY(30px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fadeInUp {
      animation: fadeInUp 1s ease forwards;
    }
    .delay-100 {
      animation-delay: 0.1s;
    }
    .delay-200 {
      animation-delay: 0.2s;
    }
    .delay-300 {
      animation-delay: 0.3s;
    }
    .delay-400 {
      animation-delay: 0.4s;
    }
  `}</style>
</section>

    


      {/* Contact Section */}
      <section className="flex-1 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <ScrollReveal>
            <div>
              <h2 className="font-display font-bold text-4xl text-gray-900 mb-10 bg-blue-500 bg-clip-text text-transparent">
                Contact Information
              </h2>

              <div className="space-y-10 mb-16">
                {/* Email */}
                <div className="flex gap-4 p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all border border-blue-100 hover:border-blue-300">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Mail size={24} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl text-gray-900 mb-2">Email</h3>
                    <a
                      href="mailto:codifyinstitute.com@gmail.com"
                      className="text-gray-600 mb-1 font-medium hover:text-blue-600 transition-colors"
                    >
                      codifyinstitute.com@gmail.com
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4 p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all border border-blue-100 hover:border-blue-300">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Phone size={24} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl text-gray-900 mb-2">Phone</h3>
                    <a
                      href="tel:+917759020121"
                      className="text-gray-600 font-medium hover:text-blue-600 transition-colors"
                    >
                      +91 7759020121
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex flex-col gap-6 p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all border border-blue-100 hover:border-blue-300">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <MapPin size={24} className="text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-xl text-gray-900 mb-2">Office</h3>
                      <a
                        href="https://www.google.com/maps/place/Codify+Institute/@19.4599555,72.7978747,17z"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 font-medium hover:text-blue-600 transition-colors"
                      >
                        Shop No. 201/202, Gold Crest 369, above Zudio, near New Viva College,
                        Virar West, Maharashtra 401303
                      </a>
                    </div>
                  </div>

                  {/* Embedded Google Map */}
                  <div className="rounded-2xl overflow-hidden border border-blue-100 h-72 shadow-inner">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3761.8981963801816!2d72.79787467595925!3d19.45995558182529!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7ab059e821c33%3A0x28d1b7b9c3d25fb9!2sCodify%20Institute!5e0!3m2!1sen!2sin!4v1761967180506!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Contact Form */}
          <ScrollReveal delay={0.1}>
            <div className="bg-white rounded-2xl p-10 shadow-2xl border border-gray-100">
              <h2 className="font-display font-bold text-3xl text-gray-900 mb-8">Send us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Enquiry Type */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">I am interested in:</label>
                  <div className="grid grid-cols-2 gap-4">
                    <label
                      className={`flex items-center justify-center cursor-pointer p-4 rounded-xl border-2 transition-all font-medium ${
                        formData.enquiryType === "general"
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                    >
                      <input
                        type="radio"
                        name="enquiryType"
                        value="general"
                        checked={formData.enquiryType === "general"}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4 accent-blue-600"
                      />
                      <span>Online Courses</span>
                    </label>
                    <label
                      className={`flex items-center justify-center cursor-pointer p-4 rounded-xl border-2 transition-all font-medium ${
                        formData.enquiryType === "offline-training"
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                    >
                      <input
                        type="radio"
                        name="enquiryType"
                        value="offline-training"
                        checked={formData.enquiryType === "offline-training"}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4 accent-blue-600"
                      />
                      <span>Offline Training</span>
                    </label>
                  </div>
                </div>

                {/* Basic Info */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                {/* Conditional Fields */}
                {formData.enquiryType === "general" ? (
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Subject</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                    >
                      <option value="">Select a topic</option>
                      <option value="course-inquiry">Course Inquiry</option>
                      <option value="technical-support">Technical Support</option>
                      {/* <option value="partnership">Partnership</option> */}
                      <option value="other">Other</option>
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">Interested Course</label>
                        <select
                          name="course"
                          value={formData.course}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                        >
                          <option value="">Select a course</option>
                          {COURSES.map((course) => (
                            <option key={course} value={course}>
                              {course}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">Preferred Location</label>
                        <select
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                        >
                          <option value="">Select location</option>
                          {LOCATIONS.map((loc) => (
                            <option key={loc} value={loc}>
                              {loc}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">Preferred Start Date</label>
                        <input
                          type="date"
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">Group Size</label>
                        <select
                          name="groupSize"
                          value={formData.groupSize}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all"
                        >
                          <option value="1">1 Person</option>
                          <option value="2-5">2-5 People</option>
                          <option value="6-10">6-10 People</option>
                          <option value="11-20">11-20 People</option>
                          <option value="20+">20+ People</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {/* Message */}
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white px-6 py-4 rounded-xl hover:bg-blue-700 transition-all font-display font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-lg"
                >
                  {loading ? (
                    <Loader2 className="animate-spin w-5 h-5" />
                  ) : (
                    <Send size={20} />
                  )}
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>

              {submitted && (
                <div className="mt-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-500 text-green-700 rounded-xl text-center font-bold shadow-lg">
                  Thank you! We'll be in touch soon.
                </div>
              )}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <StudentTestimonials />
      <Footer />
    </div>
  );
}